import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  IUserGroupFieldsFragment,
  PermissionKey,
  UserGroupType,
  UserState,
} from '../../../../../utils/apollo/graphqlSchema';
import Table from '../../../../../components/Table';
import { appRoutes } from '../../../../../path';
import GeneralFormItem from '../../../../../components/GeneralFormItem';
import UserStatus from '../../../Users/components/UserStatus';
import ActionsMenu from '../../../../../components/ActionsMenu';
import PermissionElementWrapper from '../../../../PermissionControls/PermissionElementWrapper';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { useAppDispatch } from '../../../../../redux/hooks';
import { groupsTableConfig } from './tableStylesConfig';
import { SingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';

interface IProps {
  groups: IUserGroupFieldsFragment[];
}

const getGroupIdUrl = (groupId: string) => {
  return `${appRoutes.permission.userGroups}/${groupId}`;
};

const columnHelper = createColumnHelper<IUserGroupFieldsFragment>();

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: false, // sort by name in ascending order by default
    },
  ] satisfies SortingState,
};

const UserGroupsTable = ({ groups }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { checkIsPermitted } = usePermission();
  const isPermittedToDisable = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminDelete],
  });

  const onOpenUserGroupToggleModal = useCallback(
    (userGroup: IUserGroupFieldsFragment) => {
      dispatch(
        openModal({
          type: userGroup.active
            ? ModalSliceType.DisableUserGroup
            : ModalSliceType.EnableUserGroup,
          data: userGroup,
        }),
      );
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Group Name',
        cell: (info) => {
          const name = info.getValue();
          const group = info.row.original;

          return (
            <GeneralFormItem
              label={<Link to={getGroupIdUrl(group.id)}>{name}</Link>}
              tooltipContent={name}
              isBold
              isDisabled={!group.active}
            />
          );
        },
        meta: groupsTableConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('type', {
        id: 'type',
        header: 'Group Type',
        cell: (info) => {
          const type = info.getValue();
          const group = info.row.original;
          const capitalizedType = capitalize(type);

          return (
            <GeneralFormItem
              label={capitalizedType}
              tooltipContent={capitalizedType}
              isDisabled={!group.active}
            />
          );
        },
        meta: groupsTableConfig.type,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const description = info.getValue();
          const group = info.row.original;

          return (
            <GeneralFormItem
              label={description}
              tooltipContent={description}
              isDisabled={!group.active}
            />
          );
        },
        meta: groupsTableConfig.description,
      }),
      columnHelper.accessor('roles', {
        id: 'roles',
        header: 'Role(s)',
        cell: (info) => {
          const roles = info.getValue();
          const group = info.row.original;
          const rolesDisplay = roles.map((role) => role.name).join(', ');

          return (
            <GeneralFormItem
              label={rolesDisplay}
              tooltipContent={rolesDisplay}
              isDisabled={!group.active}
            />
          );
        },
        meta: groupsTableConfig.roles,
      }),
      columnHelper.accessor('active', {
        id: 'status',
        header: 'Status',
        cell: (info) => {
          const active = info.getValue();

          return (
            <UserStatus state={active ? UserState.Active : UserState.Deleted} />
          );
        },
        meta: groupsTableConfig.status,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const group = info.row.original;

          return (
            <ActionsMenu>
              <SingleSelectOption
                onClick={() => navigate(getGroupIdUrl(group.id))}
                label={'View Group'}
              />
              {group.type !== UserGroupType.External && (
                <PermissionElementWrapper
                  isPermitted={isPermittedToDisable}
                  isHalfOpacity={true}
                >
                  <SingleSelectOption
                    disabled={!isPermittedToDisable}
                    onClick={() => {
                      onOpenUserGroupToggleModal(group);
                    }}
                    label={group.active ? 'Disable Group' : 'Enable Group'}
                  />
                </PermissionElementWrapper>
              )}
            </ActionsMenu>
          );
        },
        meta: groupsTableConfig.actions,
      }),
    ],
    [isPermittedToDisable, navigate, onOpenUserGroupToggleModal],
  );

  const table = useReactTable({
    data: groups,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default UserGroupsTable;
